import { request } from './core'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import type {
  ExternalAgent,
  ExternalAgentCreate,
  ExternalAgentUpdate,
  ExternalAgentList,
  ExternalAgentChatMessage,
  ExternalAgentChatResponse,
  ExternalAgentChatSession,
  ExternalAgentChatSessionCreate,
  ExternalAgentChatSessionList,
  ExternalAgentChatSessionMessageList,
  ExternalAgentFeedbackCreate,
  ExternalAgentMessageFeedbackResponse,
  ExternalAgentSessionFeedbackResponse,
  ConnectionTestResult,
  GithubCopilotDeviceAuthStart,
  GithubCopilotDeviceAuthPoll,
} from './types'

export const externalAgentsApi = {
  /** Unpaginated list for dropdowns/selectors. Returns all external agents matching filters. */
  listAll: (params?: {
    project_id?: string
    platform?: string
    status?: string
    search?: string
    include_store_access?: boolean
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<ExternalAgentList>(`/external-agents/list-all${query}`, { signal })
  },

  list: (params?: {
    project_id?: string
    platform?: string
    status?: string
    search?: string
    page?: number
    page_size?: number
    order_by?: string
    order_desc?: boolean
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<ExternalAgentList>(`/external-agents${query}`, { signal })
  },

  get: (id: string, options?: { signal?: AbortSignal; tenantId?: string }) =>
    request<ExternalAgent>(`/external-agents/${id}`, {
      signal: options?.signal,
      tenantId: options?.tenantId,
    }),

  create: (projectId: string, data: ExternalAgentCreate, options?: { tenantId?: string }) =>
    request<ExternalAgent>(`/projects/${projectId}/external-agents`, {
      method: 'POST',
      body: JSON.stringify(data),
      tenantId: options?.tenantId,
    }),

  update: (id: string, data: ExternalAgentUpdate, options?: { tenantId?: string }) =>
    request<ExternalAgent>(`/external-agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      tenantId: options?.tenantId,
    }),

  delete: (id: string, options?: { tenantId?: string }) =>
    request<void>(`/external-agents/${id}`, {
      method: 'DELETE',
      tenantId: options?.tenantId,
    }),

  bulkDelete: (agentIds: string[], options?: { tenantId?: string }) =>
    request<{ deleted: number; skipped: number; errors: string[] }>(
      '/external-agents/bulk-delete',
      {
        method: 'POST',
        body: JSON.stringify({ agent_ids: agentIds }),
        tenantId: options?.tenantId,
      }
    ),

  testConnection: (id: string) =>
    request<ConnectionTestResult>(`/external-agents/${id}/test-connection`, {
      method: 'POST',
    }),

  chat: (
    id: string,
    data: ExternalAgentChatMessage,
    platformOrOptions?: string | { signal?: AbortSignal }
  ) => {
    const options =
      platformOrOptions && typeof platformOrOptions === 'object' ? platformOrOptions : undefined
    return request<ExternalAgentChatResponse>(`/external-agents/${id}/chat`, {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    })
  },

  listSessions: (
    id: string,
    params?: {
      page?: number
      page_size?: number
      search?: string
      environment?: string
      source?: string
      signal?: AbortSignal
    }
  ) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return request<ExternalAgentChatSessionList>(`/external-agents/${id}/sessions${query}`, {
      signal,
    })
  },

  createSession: (id: string, data?: ExternalAgentChatSessionCreate) =>
    request<ExternalAgentChatSession>(`/external-agents/${id}/sessions`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),

  getSession: (id: string, sessionId: string, options?: { signal?: AbortSignal }) =>
    request<ExternalAgentChatSession>(`/external-agents/${id}/sessions/${sessionId}`, {
      signal: options?.signal,
    }),

  deleteSession: (id: string, sessionId: string) =>
    request<void>(`/external-agents/${id}/sessions/${sessionId}`, {
      method: 'DELETE',
    }),

  getMessages: (id: string, sessionId: string, options?: { signal?: AbortSignal }) =>
    request<ExternalAgentChatSessionMessageList>(
      `/external-agents/${id}/sessions/${sessionId}/messages`,
      { signal: options?.signal }
    ),

  /**
   * Thumbs up/down or star rating for one assistant message.
   *
   * External-agent replies are stored as TAGP ``chat_messages`` rows, so the
   * same upsert shape as Agentserve's feedback endpoints applies.
   */
  submitMessageFeedback: (
    id: string,
    sessionId: string,
    messageId: string,
    data: ExternalAgentFeedbackCreate
  ) =>
    request<ExternalAgentMessageFeedbackResponse>(
      `/external-agents/${id}/sessions/${sessionId}/messages/${messageId}/feedback`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  /** Whole-session feedback (thumbs or star rating) for an external-agent chat. */
  submitSessionFeedback: (id: string, sessionId: string, data: ExternalAgentFeedbackCreate) =>
    request<ExternalAgentSessionFeedbackResponse>(
      `/external-agents/${id}/sessions/${sessionId}/feedback`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  startGithubCopilotDeviceAuth: (options?: { signal?: AbortSignal }) =>
    request<GithubCopilotDeviceAuthStart>(
      '/external-agents/github-copilot/device-auth/start',
      { method: 'POST', retry: true, signal: options?.signal }
    ),

  pollGithubCopilotDeviceAuth: (sessionId: string, options?: { signal?: AbortSignal }) =>
    request<GithubCopilotDeviceAuthPoll>(
      `/external-agents/github-copilot/device-auth/${encodeURIComponent(sessionId)}`,
      { signal: options?.signal }
    ),

  listDatabricksApps: (params: {
    connection_id?: string
    iam_id?: string
    workspace_url?: string
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params
    const query = buildQueryString(queryParams)
    return request<{ apps: Array<{
      name: string
      url?: string | null
      description?: string | null
      compute_state?: string | null
      app_state?: string | null
    }> }>(`/external-agents/databricks/apps${query}`, { signal })
  },

  registerFromDatabricksApp: (data: {
    project_id: string
    connection_id?: string
    iam_id?: string
    workspace_url?: string
    app_name: string
    name?: string
  }) =>
    request<ExternalAgent>('/external-agents/from-databricks-app', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  listDataikuAgents: (params: {
    iam_id?: string
    project_key?: string
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params
    const query = buildQueryString(queryParams)
    return request<{
      project_key: string
      agents: Array<{
        agent_id: string
        llm_id: string
        display_name: string
        description?: string | null
      }>
    }>(`/external-agents/dataiku/agents${query}`, { signal })
  },

  registerFromDataikuAgent: (data: {
    project_id: string
    iam_id?: string
    project_key?: string
    agent_id: string
    name?: string
    description?: string
  }) =>
    request<ExternalAgent>('/external-agents/from-dataiku-agent', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  listSnowflakeCortexAgents: (params: {
    connection_id: string
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params
    const query = buildQueryString(queryParams)
    return request<{ agents: Array<{ name?: string; comment?: string | null }> }>(
      `/external-agents/snowflake/cortex${query}`,
      { signal },
    )
  },

  registerFromSnowflakeCortex: (data: {
    project_id: string
    connection_id?: string
    iam_id?: string
    agent_name: string
    name?: string
  }) =>
    request<ExternalAgent>('/external-agents/from-snowflake-cortex', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}
